import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../_metronic/helpers'
import { FaFileAlt, FaEnvelope } from 'react-icons/fa'
import ReceiptVaultFileDropzone from './ReceiptVaultFileDropzone'
import tosterKey from '../../localization/toster.json'
import labelKey from '../../localization/label.json'
import { useUploadReceiptsForUserMutation } from '../../../services/InvoiceDataDraftApi'
import { useGetAllUsersManagerAdminQuery } from '../../../services/GeneralApi'
import SearchSelect from '../../../components/textFields/SearchSelect'
import heic2any from 'heic2any'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import { useGetReceiptVaultUploadRulesQuery } from '../../../services/authApi'
import ReceiptVaultRulesAcknowledgeModal from './ReceiptVaultRulesAcknowledgeModal'
import ReceiptVaultUploadRulesPanel from './ReceiptVaultUploadRulesPanel'

type Props = {
    show: boolean
    handleClose: () => void
}

type FileType = 'image' | 'pdf' | 'msg' | 'eml' | 'heic' | 'unknown'

const UploadReceiptForUserModal = ({ show, handleClose }: Props) => {
    const {
        isReceiptVaultRulesDisplayOnUploadPopup,
        isReceiptVaultRulesAcknowledgementRequired,
        isReceiptVaultRulesAcknowledgementAccepted,
    } = useAppSelector(selectAuth)
    const { data: rulesMeta } = useGetReceiptVaultUploadRulesQuery(undefined, {
        skip: !show,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
    })

    const displayRulesOnPopup =
        rulesMeta?.result?.displayOnUploadPopup ?? !!isReceiptVaultRulesDisplayOnUploadPopup
    const acknowledgementRequired =
        rulesMeta?.result?.acknowledgementRequired ?? !!isReceiptVaultRulesAcknowledgementRequired
    const needsRulesAck =
        !!displayRulesOnPopup && !!acknowledgementRequired && !isReceiptVaultRulesAcknowledgementAccepted
    const [rulesAckPassed, setRulesAckPassed] = useState(false)

    const [uploadFile, { isLoading: uploadFileIsLoading, isSuccess: uploadFileIsSuccess, isError, error }] =
        useUploadReceiptsForUserMutation()
    const { data: getUserByManager } = useGetAllUsersManagerAdminQuery('')

    const fileInputRef = useRef<HTMLInputElement>(null)
    const [filePreviews, setFilePreviews] = useState<(string | null)[]>([])
    const [fileTypes, setFileTypes] = useState<FileType[]>([])
    const [fileNames, setFileNames] = useState<string[]>([])
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [isCorpCreditCardItem, setisCorpCreditCardItem] = useState<string>('')
    const [isCorpCreditCardItemError, setisCorpCreditCardItemError] = useState<string>('')
    const [orgUserId, setOrgUserId] = useState<string | number>('')
    const [orgUserIdError, setOrgUserIdError] = useState('')

    useEffect(() => {
        if (show) {
            setRulesAckPassed(!needsRulesAck)
        }
    }, [show, needsRulesAck])

    const showUploadForm = show && (!needsRulesAck || rulesAckPassed)
    const showAckModal = show && needsRulesAck && !rulesAckPassed

    const handleAckCancel = () => {
        setRulesAckPassed(false)
        handleClose()
    }

    const getFileType = (file: File): FileType => {
        const type = file.type.toLowerCase()
        const name = file.name.toLowerCase()
        if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
        if (type === 'application/vnd.ms-outlook' || name.endsWith('.msg')) return 'msg'
        if (type === 'message/rfc822' || name.endsWith('.eml')) return 'eml'
        if (name.endsWith('.heic') || name.endsWith('.heif')) return 'heic'
        if (type.startsWith('image/')) return 'image'
        return 'unknown'
    }

    const getFileIcon = (type: FileType) => {
        switch (type) {
            case 'pdf':
                return <FaFileAlt className="text-danger" style={{ fontSize: '48px' }} />
            case 'msg':
            case 'eml':
                return <FaEnvelope className="text-primary" style={{ fontSize: '48px' }} />
            case 'heic':
                return <FaFileAlt className="text-warning" style={{ fontSize: '48px' }} />
            default:
                return <FaFileAlt className="text-secondary" style={{ fontSize: '48px' }} />
        }
    }

    const getFileExtensionLabel = (type: FileType) => {
        switch (type) {
            case 'pdf': return 'PDF'
            case 'msg': return 'MSG'
            case 'eml': return 'EML'
            case 'image': return 'Image'
            case 'heic': return 'HEIC'
            default: return 'File'
        }
    }

    const processSelectedFiles = (selectedFilesArray: File[]) => {
        if (!selectedFilesArray.length) return

        const newPreviews: (string | null)[] = []
        const newFileTypes: FileType[] = []
        const newFileNames: string[] = []
        setSelectedFiles(selectedFilesArray)

        selectedFilesArray.forEach((file, index) => {
            const fileType = getFileType(file)
            newFileTypes.push(fileType)
            newFileNames.push(file.name)

            if (fileType === 'image' || fileType === 'heic') {
                if (fileType === 'heic') {
                    heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 })
                        .then((convertedBlob: any) => {
                            const reader = new FileReader()
                            reader.onload = () => {
                                newPreviews[index] = reader.result as string
                                if (newPreviews.filter((p) => p !== undefined).length === selectedFilesArray.length) {
                                    setFilePreviews([...newPreviews])
                                }
                            }
                            reader.readAsDataURL(convertedBlob as Blob)
                        })
                        .catch(() => {
                            newPreviews[index] = null
                            if (newPreviews.filter((p) => p !== undefined).length === selectedFilesArray.length) {
                                setFilePreviews([...newPreviews])
                            }
                        })
                } else {
                    const reader = new FileReader()
                    reader.onload = () => {
                        newPreviews[index] = reader.result as string
                        if (newPreviews.filter((p) => p !== undefined).length === selectedFilesArray.length) {
                            setFilePreviews([...newPreviews])
                        }
                    }
                    reader.readAsDataURL(file)
                }
            } else {
                newPreviews[index] = null
                if (newPreviews.filter((p) => p !== undefined).length === selectedFilesArray.length) {
                    setFilePreviews([...newPreviews])
                }
            }
        })

        setFileTypes(newFileTypes)
        setFileNames(newFileNames)
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!orgUserId) {
            setOrgUserIdError('Please select a user')
            return
        }
        setOrgUserIdError('')

        if (!isCorpCreditCardItem) {
            setisCorpCreditCardItemError('Please select a transaction type')
            return
        }
        setisCorpCreditCardItemError('')

        if (selectedFiles.length === 0) {
            toast.error('Please select at least one file')
            return
        }

        const formData = new FormData()
        selectedFiles.forEach((file) => formData.append('files', file))
        formData.append('orgUserId', String(orgUserId))
        formData.append('isCorpCreditCardItem', isCorpCreditCardItem)
        await uploadFile(formData)
    }

    const handleRemoveFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
        setFilePreviews((prev) => prev.filter((_, i) => i !== index))
        setFileTypes((prev) => prev.filter((_, i) => i !== index))
        setFileNames((prev) => prev.filter((_, i) => i !== index))
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const resetForm = () => {
        setFilePreviews([])
        setSelectedFiles([])
        setFileTypes([])
        setFileNames([])
        setisCorpCreditCardItem('')
        setisCorpCreditCardItemError('')
        setOrgUserId('')
        setOrgUserIdError('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    useEffect(() => {
        if (uploadFileIsSuccess) {
            toast.success(tosterKey.receiptAddedSuccessfully)
            resetForm()
            handleClose()
        }
    }, [uploadFileIsSuccess])

    useEffect(() => {
        if (isError && error) {
            let errorMessage = 'Something went wrong'
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message
            }
            toast.error(errorMessage)
        }
    }, [isError, error])

    const handleModalClose = () => {
        resetForm()
        handleClose()
    }

    const renderFilePreview = (preview: string | null, fileType: FileType, fileName: string, index: number) => {
        if ((fileType === 'image' || fileType === 'heic') && preview) {
            return (
                <img
                    src={preview}
                    alt={fileName}
                    className='w-100 p-2'
                    style={{ height: '280px', objectFit: 'contain' }}
                />
            )
        }
        return (
            <div className="d-flex flex-column align-items-center justify-content-center p-5 border rounded-3 bg-light" style={{ height: '280px' }}>
                <div className="mb-3">{getFileIcon(fileType)}</div>
                <div className="text-center">
                    <h5 className="mb-1">{fileName}</h5>
                    <span className="badge bg-secondary">{getFileExtensionLabel(fileType)}</span>
                    <p className="text-muted mt-2 mb-0">
                        <small>File size: {((selectedFiles[index]?.size || 0) / 1024).toFixed(2)} KB</small>
                    </p>
                </div>
            </div>
        )
    }

    return (
        <>
        <ReceiptVaultRulesAcknowledgeModal
            show={showAckModal}
            onAcknowledged={() => setRulesAckPassed(true)}
            onCancel={handleAckCancel}
        />
        <Modal
            aria-hidden='true'
            dialogClassName='modal-dialog modal-lg modal-dialog-centered'
            show={showUploadForm}
            onHide={handleModalClose}
        >
            <form className='form w-100' onSubmit={handleSubmit}>
                <div className='modal-header'>
                    <h2 className='mb-0'>Upload Receipt for User</h2>
                    <div className='d-flex gap-3'>
                        <span className='btn btn-light btn-sm' onClick={handleModalClose}>
                            {labelKey.cancel}
                        </span>
                        <button
                            type='submit'
                            className='btn btn-primary btn-sm'
                            disabled={uploadFileIsLoading}
                        >
                            {!uploadFileIsLoading && (
                                <span className='indicator-label'>{labelKey.submit}</span>
                            )}
                            {uploadFileIsLoading && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    {labelKey.pleaseWait}...
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                <div className='modal-body py-lg-10 px-lg-10'>
                    {/* Live org setting IsReceiptVaultRulesDisplayOnUploadPopup via GetReceiptVaultUploadRules */}
                    {!needsRulesAck ? <ReceiptVaultUploadRulesPanel show={showUploadForm} /> : null}
                    <div className='alert alert-info d-flex align-items-center p-3 mb-5' role='alert'>
                        <KTSVG path='/media/icons/duotune/general/gen064.svg' className='svg-icon-2 me-3' />
                        <div>Select a user and upload receipts into their receipt vault (same as Receipt Vault upload).</div>
                    </div>

                    <div className='mb-5'>
                        <SearchSelect
                            label='User'
                            options={(getUserByManager?.result || []).map((option: any) => ({
                                value: option.id,
                                label: option?.value,
                            }))}
                            placeholder='Select User'
                            isClearable
                            value={
                                orgUserId
                                    ? {
                                          value: orgUserId,
                                          label:
                                              getUserByManager?.result?.find((o: any) => o.id === orgUserId)?.value ||
                                              orgUserId,
                                      }
                                    : null
                            }
                            onChange={(selected: any) => {
                                setOrgUserId(selected?.value || '')
                                if (selected?.value) setOrgUserIdError('')
                            }}
                        />
                        {orgUserIdError && <div className='text-danger fs-7 mt-1'>{orgUserIdError}</div>}
                    </div>

                    <div className='mb-5'>
                        <label className='required form-label fw-bold'>Is This a Corporate Credit Card Transaction</label>
                        <select
                            className={`form-select form-select-solid ${isCorpCreditCardItemError ? 'is-invalid' : ''}`}
                            value={isCorpCreditCardItem}
                            onChange={(e) => {
                                setisCorpCreditCardItem(e.target.value)
                                if (e.target.value) setisCorpCreditCardItemError('')
                            }}
                        >
                            <option value=''>Please select</option>
                            <option value='4'>Yes</option>
                            <option value='1'>No</option>
                        </select>
                        {isCorpCreditCardItemError && (
                            <div className='invalid-feedback'>{isCorpCreditCardItemError}</div>
                        )}
                    </div>

                    <div className='mb-5'>
                        {selectedFiles.length === 0 && (
                            <ReceiptVaultFileDropzone
                                inputRef={fileInputRef}
                                onFilesSelected={processSelectedFiles}
                            />
                        )}

                        {filePreviews.map((preview, index) => (
                            <div key={index} className='position-relative mb-3'>
                                <div
                                    className='btn btn-icon btn-bg-light hover-danger btn-sm position-absolute m-4'
                                    style={{ left: 10, zIndex: 12, top: '13px' }}
                                    onClick={() => handleRemoveFile(index)}
                                >
                                    <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-hover-danger' />
                                </div>
                                {renderFilePreview(preview, fileTypes[index], fileNames[index], index)}
                            </div>
                        ))}
                    </div>

                    {selectedFiles.length > 1 && (
                        <div className="alert alert-primary d-flex align-items-center" role="alert">
                            <i className="fas fa-info-circle me-2"></i>
                            {selectedFiles.length} file(s) selected for upload
                        </div>
                    )}
                </div>
            </form>
        </Modal>
        </>
    )
}

export default UploadReceiptForUserModal
